import {
  Dispatch,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import {Location, History} from "history";
import {Box, Col, css, DSIconButton, DSIconClose, Row, StyleProps, DSSpinner} from "@cdx/ds";
import {cx, useEsc, useLocalStorageState, useWindowSize} from "@cdx/common";
import {getVarName} from "@vanilla-extract/private";
import {makeScrollable} from "@cdx/ds/utils/makeScrollable";
import {openSidePanel} from "./card-container-utils";
import {
  cardContainerStyles,
  cardContainerVars,
  dragBorderStyles,
} from "./unified-card-container.css";
import {setupDragHandlers} from "../../lib/hooks/use-drag";

const targetWidth: Record<string, number> = {
  comments: 450,
  attachments: 380,
  childCards: 380,
};

type SidepanelContainerProps = {
  children: ReactNode;
  onClose: () => void;
  sidePanelKey: string;
};
const SidepanelContainer = (props: SidepanelContainerProps) => {
  const {children, onClose, sidePanelKey} = props;
  useEsc(onClose);
  return (
    <Col
      absolute
      top="0"
      left="100%"
      bottom="0"
      colorTheme="gray50"
      bg="foreground"
      maxHeight="100%"
      style={{width: targetWidth[sidePanelKey] || 300}}
      borderWidth={0}
      borderRightWidth={1}
      borderColor="default"
      flex="none"
    >
      <Box
        className={cx(
          makeScrollable(),
          cardContainerStyles.fullHeight,
          cardContainerStyles.stickyTop
        )}
        position="sticky"
        overscrollBehaviour="contain"
      >
        <Suspense
          fallback={
            <Col align="center" justify="center" absolute inset="0">
              <DSSpinner size={24} />
            </Col>
          }
        >
          {children}
        </Suspense>
      </Box>
    </Col>
  );
};

type SidepanelButtonContainerProps = {
  sidePanels: SidePanel[];
  sidePanelKey: string | null;
  onClose: () => void;
  category: string;
  location: Location;
  history: History;
  bottom?: ReactNode;
};
const SidepanelButtonContainer = (props: SidepanelButtonContainerProps) => {
  const {sidePanels, onClose, sidePanelKey, category, location, history, bottom} = props;
  return (
    <Col sp="12px" align="center" py="12px">
      <DSIconButton variant="tertiary" size="md" onClick={onClose} icon={<DSIconClose />} />
      <Col sp="8px">
        {sidePanels.map(({key, button}) => (
          <button.comp
            key={key}
            active={key === sidePanelKey}
            sidePanelKey={key}
            category={category}
            location={location}
            history={history}
            {...button.props}
          />
        ))}
      </Col>
      {bottom}
    </Col>
  );
};

const FREE_ROOM = 800; // excluding sidebar
const clampWidth = (val: number, windowWidth: number) => {
  const maxCardRoom = windowWidth - FREE_ROOM;
  return Math.max(405, Math.min(maxCardRoom, val, 800));
};

export const useSidebarWidth = () => {
  const [rawWidth, setWidth] = useLocalStorageState("card-container-width", 405);
  const {width: windowWidth} = useWindowSize();
  return {width: clampWidth(rawWidth, windowWidth), setWidth, windowWidth};
};

export const DragBorder = (props: {
  containerRef: MutableRefObject<HTMLElement | null>;
  width: number;
  windowWidth: number;
  setWidth: Dispatch<SetStateAction<number>>;
}) => {
  const {containerRef, setWidth} = props;
  const [isDown, setIsDown] = useState(false);
  const propRef = useRef(props);
  propRef.current = props;

  const [{handlers, unsubs}] = useState(() =>
    setupDragHandlers(
      (startPos) => {
        setIsDown(true);
        let dx = 0;
        return {
          onMove: ([x]) => {
            dx = x - startPos[0];
            const n = containerRef.current;
            if (!n) return;
            const nextWidth = clampWidth(propRef.current.width + dx, propRef.current.windowWidth);
            n.style.width = `${nextWidth}px`;
          },
          onStop: (isCancelled) => {
            if (isCancelled) {
              const n = containerRef.current;
              if (n) n.style.width = `${propRef.current.width}px`;
            } else {
              const nextWidth = clampWidth(propRef.current.width + dx, propRef.current.windowWidth);
              setWidth(nextWidth);
            }
            setIsDown(false);
          },
        };
      },
      () => {
        setIsDown(true);
        return {
          onDone: () => null,
          onCancel: () => {
            setIsDown(false);
          },
        };
      }
    )
  );
  useEffect(() => {
    return () => unsubs.forEach((fn) => fn());
  }, [unsubs]);

  return (
    <Box width="0" relative borderWidth={0} borderRightWidth={1} borderColor="default">
      <Box
        className={cx(dragBorderStyles.bar, dragBorderStyles.state[isDown ? "down" : "up"])}
        {...handlers}
      />
    </Box>
  );
};

export const RawCardContainer = ({
  contextName,
  children,
  boxProps,
  width,
  withParentPill,
  withCoverImage,
}: {
  contextName: string;
  children: ReactNode;
  boxProps?: StyleProps;
  width: number;
  withParentPill?: boolean;
  withCoverImage?: boolean;
}) => {
  return (
    <Box
      relative
      flex="none"
      data-cdx-context={contextName}
      className={cx(
        cardContainerStyles.outerHeight,
        withParentPill && cardContainerStyles.withParentPill,
        withCoverImage && cardContainerStyles.withCoverImage
      )}
      style={{
        width,
        [getVarName(cardContainerVars.containerWidth)]: `${width}px`,
      }}
      zIndex={1}
    >
      <Row absolute left="0" top="0" height="100%" bg="foreground" {...boxProps}>
        {children}
      </Row>
    </Box>
  );
};

type ContainerProps = {
  children: ReactNode;
  contextName: string;
  sidePanelKey: string | null;
  sidePanels: SidePanel[];
  onClose: () => void;
  onCloseSidePanel: () => void;
  category: string;
  location: Location;
  history: History;
  sideBottom?: ReactNode;
  uploadArea?: ReactNode;
  withParentPill?: boolean;
  withCoverImage?: boolean;
};
const Container = (props: ContainerProps) => {
  const {
    children,
    contextName,
    sidePanelKey,
    sidePanels,
    onClose,
    category,
    location,
    history,
    sideBottom,
    uploadArea,
    onCloseSidePanel: closeSidePanel,
    withParentPill,
    withCoverImage,
  } = props;
  const {width, setWidth, windowWidth} = useSidebarWidth();

  const currSidePanel = (sidePanelKey && sidePanels.find((sp) => sp.key === sidePanelKey)) ?? null;
  const boxProps = currSidePanel ? ({colorTheme: "gray25"} as const) : {};
  const containerRef = useRef<HTMLElement | null>(null);

  return (
    <>
      {currSidePanel && (
        <div
          onClick={closeSidePanel}
          data-cdx-clickable
          className={css({
            position: "absolute",
            top: "0",
            bottom: "0",
            zIndex: 1,
          })}
          style={{background: `#40465ACC`, left: width, right: -16}}
        />
      )}
      <RawCardContainer
        contextName={contextName}
        boxProps={boxProps}
        width={width}
        withParentPill={withParentPill}
        withCoverImage={withCoverImage}
      >
        {uploadArea}
        <Box px="12px" relative style={{width}} ref={containerRef} zIndex={1} bg="foreground">
          <Row
            className={cx(cardContainerStyles.stickyTop, cardContainerStyles.fullHeight)}
            position="sticky"
            sp="12px"
            flex="auto"
          >
            <Col flex="auto" maxHeight="100%" pt="12px" pb="16px" sp="12px" minWidth="0">
              {children}
            </Col>
            <SidepanelButtonContainer
              sidePanelKey={sidePanelKey}
              sidePanels={sidePanels}
              onClose={onClose}
              category={category}
              location={location}
              history={history}
              bottom={sideBottom}
            />
          </Row>
        </Box>
        <DragBorder
          width={width}
          containerRef={containerRef}
          setWidth={setWidth}
          windowWidth={windowWidth}
        />
        {currSidePanel && (
          <SidepanelContainer
            onClose={() => openSidePanel(history, location, sidePanelKey, true)}
            sidePanelKey={currSidePanel.key}
          >
            {currSidePanel.content}
          </SidepanelContainer>
        )}
      </RawCardContainer>
    </>
  );
};

type SidePanelButton = {comp: (props: any) => JSX.Element; props?: any};
type SidePanel<TPanel extends string = string> = {
  key: TPanel;
  button: SidePanelButton;
  content: ReactNode;
};

export type UnifiedCardContainerOpts<TPanel extends string> = {
  initialSidePanel?: TPanel | null;
  location: Location;
  history: History;
  onClose: () => void;
  category: string;
  sidePanels: SidePanel<TPanel>[];
  sideBottom?: ReactNode;
};
const useUnifiedCardContainer = <TPanel extends string>(opts: UnifiedCardContainerOpts<TPanel>) => {
  const {history, onClose, sidePanels, category, sideBottom} = opts;
  const location = opts.location as Location<{sidePanelKey?: TPanel | null}>;

  const lastSidePanelKeyRef = useRef<TPanel | null | undefined>(undefined);
  const locBasedSidePanelKey = location.state?.sidePanelKey;
  const sidePanelKey =
    (locBasedSidePanelKey === undefined ? lastSidePanelKeyRef.current : locBasedSidePanelKey) ??
    null;

  const allOpts = {sidePanelKey, ...opts};
  const optsRef = useRef(allOpts);
  useEffect(() => {
    optsRef.current = allOpts;
  });

  useEffect(() => {
    // when opening a a card without location state, we keep the current side panel open
    if (lastSidePanelKeyRef.current && locBasedSidePanelKey === undefined) {
      openSidePanel(history, optsRef.current.location, lastSidePanelKeyRef.current);
    } else {
      lastSidePanelKeyRef.current = locBasedSidePanelKey;
    }
  }, [locBasedSidePanelKey, history]);

  useEffect(() => {
    const initial = optsRef.current.initialSidePanel ?? null;
    if (initial && initial !== optsRef.current.sidePanelKey) {
      openSidePanel(history, optsRef.current.location, initial);
    }
  }, [history]);

  useEsc(onClose);
  return {
    Container,
    containerProps: {
      sidePanelKey,
      sidePanels: sidePanels,
      onClose,
      category,
      location,
      history,
      sideBottom,
      onCloseSidePanel: () => openSidePanel(history, location, null, true),
    },
    sidePanelKey,
  };
};

export default useUnifiedCardContainer;
